/* ─── Vote feature-identifier convention (MS-700 AC-3) ───
   R-OBS-1 (mambu-sparks-main/Roadmap/PRDs/Agent Studio/agent-studio-prd.md:208)
   defines the interest-vote event as feature / direction / tenant / user /
   timestamp — there is NO surface or page field. The feature identifier is
   therefore the only place the surface can be recorded, so it carries it:

       "Mambu Payments MCP"              → vote cast on MCP Config
       "Mambu Payments MCP (Analytics)"  → vote cast on MCP Analytics

   Anyone reading these events in Grafana depends on that convention, because it
   cannot be inferred from the payload. To total demand for one product across
   both surfaces, PREFIX-match the product name rather than exact-matching the
   identifier.

   Two rules for whoever touches this next:
   1. Do NOT drop the "(Analytics)" suffix to make the two surfaces match. That
      would permanently destroy the page dimension, and lost data is not
      recoverable while a prefix match is merely a query annoyance. (If the spec
      later gains a first-class `surface` attribute on the event, both surfaces
      can move to bare product names and this helper goes away.)
   2. Do NOT retrofit a "(Config)" suffix onto MCP Config — that would break the
      existing Grafana history for no gain.

   These strings are ANALYTICS KEYS, not UI copy: deliberately hardcoded English,
   and they must never go through t(). Localising them would make the emitted
   identifier depend on the admin's locale and shatter the signal across
   languages. The repo's "never hardcode a UI string" invariant does not apply —
   this value is never rendered. The user-facing product name comes from the
   catalog response (`item.name`) instead. */

export type McpVoteSurface = 'config' | 'analytics'

/* Explicit map, not a binary ternary: a ternary silently folds every unknown
   product key onto "Mambu Insights MCP", which would pollute that series in
   Grafana — invisible until someone reads the metrics, because this file
   protects a demand signal that cannot be re-derived. On a miss, fall back to
   the raw product key so a genuinely new product becomes its OWN series
   instead of joining an existing one. */
const PRODUCTS: Record<string, string> = {
  payments: 'Mambu Payments MCP',
  insights: 'Mambu Insights MCP',
}

export function mcpPreviewVoteFeature(productKey: string, surface: McpVoteSurface): string {
  const product = PRODUCTS[productKey] ?? productKey
  return surface === 'analytics' ? `${product} (Analytics)` : product
}
